import type { PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { Sale, SaleStatus } from "../types/sales.types";

export type SaleCreatePayload = Record<string, unknown>;

export type SchedulePaymentPayload = {
	amount: number;
	paymentMethod: string;
	receiptNumber: string;
	paymentDate: string;
	note?: string;
	saleId: string;
	scheduleId: string;
};

export type EarlySchedulePaymentPayload = {
	monthsCount: number;
	paymentMethod: string;
	receiptNumber: string;
	paymentDate: string;
	note?: string;
	saleId: string;
};

export type SaleCancelPayload = {
	reason: string;
	saleId: string;
};

// Backend API response shapes
export interface BackendScheduleItem {
	id?: string;
	amount?: number;
	paidAmount?: number;
	isPaid?: boolean;
	dueDate?: string;
	paidAt?: string;
}

export interface BackendSale {
	id: string;
	saleNumber?: string | number | null;
	clientId?: string;
	client?: {
		firstName?: string;
		lastName?: string;
		surname?: string | null;
		phoneNumber?: string;
	};
	roomId?: string;
	room?: {
		building?: { name?: string };
		block?: { name?: string };
		roomNumber?: string;
		floor?: number;
		area?: number;
	};
	contractPrice?: number;
	discount?: number;
	finalPrice?: number;
	initialPayment?: number;
	paymentType?: string;
	monthsCount?: number;
	scheduleStartDate?: string;
	monthlyPayment?: number;
	manager?: { firstName?: string };
	status?: string;
	createdAt?: string;
	schedules?: BackendScheduleItem[];
}

// Mapping function to adapt backend sale to frontend Sale type
function mapBackendSale(data: BackendSale): Sale {
	// The initial payment is a real schedule row (like any monthly row) that starts unpaid;
	// paid amount/percent must reflect what's actually been recorded, not the planned amount.
	const paidAmount = (data.schedules || []).reduce((sum, s) => sum + (s.paidAmount || 0), 0);
	const finalPrice = data.finalPrice || 0;
	const paidPercent = finalPrice > 0 ? Math.round((paidAmount / finalPrice) * 100) : 0;

	return {
		id: data.id,
		saleNumber:
			data.saleNumber !== undefined && data.saleNumber !== null
				? String(data.saleNumber)
				: data.id.substring(0, 6).toUpperCase(),
		clientId: data.clientId || "",
		clientName: data.client?.firstName
			? [data.client.lastName, data.client.firstName, data.client.surname].filter(Boolean).join(" ")
			: "Ism yo'q (Backend)",
		phone: data.client?.phoneNumber || "-",
		buildingId: data.roomId || "",
		buildingName: data.room?.building?.name || "Bino yo'q (Backend)",
		blockId: data.roomId || "",
		blockName: data.room?.block?.name || "Blok yo'q",
		roomId: data.roomId || "",
		roomNumber: data.room?.roomNumber || "Xona (Backend)",
		floor: data.room?.floor || 0,
		area: data.room?.area ? Number(data.room.area) : 0,
		basePrice: data.contractPrice || 0,
		discount: data.discount || 0,
		finalPrice: data.finalPrice || 0,
		downPayment: data.initialPayment || 0,
		paymentType: data.paymentType as "naqd" | "muddatli",
		monthsCount: data.monthsCount || 0,
		startDate: data.scheduleStartDate ? data.scheduleStartDate.split("T")[0] : "",
		monthlyPayment: data.monthlyPayment || 0,
		paidAmount,
		paidPercent,
		managerName: data.manager?.firstName || "Menejer yo'q (Backend)",
		status: (data.status as SaleStatus | undefined) || "faol",
		createdAt: data.createdAt ? data.createdAt.split("T")[0] : "",
		schedule: [...(data.schedules || [])]
			.sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
			.map((s: BackendScheduleItem, idx: number) => {
				const plannedAmount = s.amount || 0;
				const paidAmount = s.paidAmount || 0;

				let status: "paid" | "expected" | "planned" | "delayed" = "expected";
				if (s.isPaid) {
					status = "paid";
				} else if (s.dueDate) {
					const due = new Date(s.dueDate);
					due.setHours(23, 59, 59, 999);
					if (due < new Date()) {
						status = "delayed";
					}
				}

				let delayDays = 0;
				if (!s.isPaid && s.dueDate) {
					const due = new Date(s.dueDate);
					due.setHours(0, 0, 0, 0);
					const today = new Date();
					today.setHours(0, 0, 0, 0);
					if (due < today) {
						const diffTime = today.getTime() - due.getTime();
						delayDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					}
				}

				return {
					id: s.id,
					index: idx + 1,
					date: s.dueDate ? s.dueDate.split("T")[0] : "",
					plannedAmount,
					paidAmount,
					status,
					delayDays,
					paidAt: s.paidAt ? s.paidAt.split("T")[0] : undefined,
				};
			}),
		auditTrail: [], // Vaqtincha
	};
}

export type SalesListParams = {
	search?: string;
	status?: string;
	managerId?: string;
	clientId?: string;
	limit?: number;
	page?: number;
	orgId?: string;
};

export async function getSales(params?: SalesListParams) {
	const response = await apiClient.get<PaginatedResponse<BackendSale>>(endpoints.sales.list, {
		params,
	});

	return {
		items: response.data.data.items.map(mapBackendSale),
		meta: response.data.data.meta,
	};
}

export async function getSaleById(id: string) {
	const response = await apiClient.get<SuccessResponse<BackendSale>>(endpoints.sales.detail(id));
	return mapBackendSale(response.data.data);
}

export async function submitSaleCreate(payload: SaleCreatePayload) {
	const backendPayload = {
		clientId: payload.clientId,
		roomId: payload.roomId,
		contractPrice: payload.basePrice ? Math.round(Number(payload.basePrice)) : 0,
		discount: payload.discount ? Math.round(Number(payload.discount)) : 0,
		currency: "UZS",
		paymentType: payload.paymentType,
		initialPayment: payload.downPayment ? Math.round(Number(payload.downPayment)) : 0,
		monthsCount: payload.monthsCount,
		scheduleStartDate: payload.startDate,
		roomChoice: "tamirsiz", // backend defaults
		isKadastr: false,
		kadastrPrice: 0,
		kadastrPercent: 0,
	};

	const response = await apiClient.post<SuccessResponse<BackendSale> | BackendSale>(
		endpoints.sales.create,
		backendPayload
	);
	const sale = "success" in response.data ? response.data.data : response.data;
	return mapBackendSale(sale);
}

export async function submitSchedulePayment(payload: SchedulePaymentPayload) {
	const response = await apiClient.post<SuccessResponse<Record<string, unknown>>>(
		endpoints.payments.create,
		{
			saleId: payload.saleId,
			scheduleIds: [payload.scheduleId],
			amount: Math.round(payload.amount),
			discountAmount: 0,
			monthsPaid: 1,
			paymentMethod: payload.paymentMethod,
			paymentDate: payload.paymentDate,
			receiptNumber: payload.receiptNumber,
			note: payload.note,
		}
	);
	return response.data.data;
}

export async function submitEarlySchedulePayment(payload: EarlySchedulePaymentPayload) {
	const response = await apiClient.post<SuccessResponse<Record<string, unknown>>>(
		endpoints.payments.early,
		payload
	);
	return response.data.data;
}

export async function submitSaleCancel(payload: SaleCancelPayload) {
	const response = await apiClient.post<SuccessResponse<Record<string, unknown>>>(
		endpoints.sales.cancel(payload.saleId),
		{ reason: payload.reason }
	);
	return response.data.data;
}

export async function submitSaleDelete(saleId: string): Promise<void> {
	await apiClient.delete<void>(endpoints.sales.detail(saleId));
}
